import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { colors, spacing, fonts, SERIF } from "@/theme";

export default function CompleteProfile() {
  const { user, needsRole, completeProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("driver");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!needsRole && user.role) {
      navigate(user.role === "driver" ? "/pending" : "/board", { replace: true });
    }
  }, [user, needsRole, navigate]);

  if (!user || (!needsRole && user.role)) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if ((role === "manager" || role === "plant") && !code.trim()) {
      setErr(role === "manager" ? "Manager invite code required" : "Plant user invite code required");
      return;
    }
    setBusy(true);
    try {
      const u = await completeProfile(role, code.trim() || undefined);
      navigate(u.role === "driver" ? "/pending" : "/board", { replace: true });
    } catch (e2) {
      setErr(e2?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.hero}>
          <img
            src="https://customer-assets.emergentagent.com/job_yard-logistics-hub-1/artifacts/mue4tej5_GSGROUP-LOGO-RGB_MAIN.jpg"
            alt="GS Group"
            style={s.logo}
          />
          <div style={s.brandTag}>GS Group – Yard Management</div>
        </div>
        <form style={s.body} onSubmit={submit}>
          <div style={s.welcome}>Welcome, {user.name}</div>
          <div style={s.help}>Choose your role to finish setting up your account.</div>

          <div style={s.label}>Role</div>
          <div style={s.roleRow}>
            {["driver", "plant", "manager"].map((r) => {
              const active = role === r;
              return (
                <button type="button" key={r} data-testid={`cp-role-${r}`} onClick={() => setRole(r)}
                  style={{ ...s.roleBtn, ...(active ? s.roleBtnActive : {}) }}>
                  <span style={{ ...s.roleText, color: active ? colors.onBrandPrimary : colors.onSurface }}>
                    {r === "manager" ? "Manager" : r === "plant" ? "Plant User" : "Driver"}
                  </span>
                </button>
              );
            })}
          </div>

          {(role === "manager" || role === "plant") && (
            <>
              <div style={s.label}>{role === "manager" ? "Manager invite code" : "Plant user invite code"}</div>
              <input data-testid="cp-invite-code" style={s.input} value={code}
                onChange={(e) => setCode(e.target.value)} placeholder="Provided by your admin" />
            </>
          )}

          {err && <div style={s.errBox} data-testid="cp-error"><span style={s.errText}>{err}</span></div>}

          <button type="submit" data-testid="cp-submit" disabled={busy}
            style={{ ...s.cta, ...(busy ? { opacity: 0.6 } : {}) }}>
            <span style={s.ctaText}>{busy ? "…" : "Continue →"}</span>
          </button>

          <button type="button" data-testid="cp-signout" style={s.signOut} onClick={doSignOut}>
            <span style={s.signOutText}>Sign out</span>
          </button>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", backgroundColor: colors.surface, display: "flex", alignItems: "center", justifyContent: "center", padding: spacing.lg, fontFamily: SERIF },
  card: { width: "100%", maxWidth: 460, backgroundColor: colors.surfaceSecondary, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface },
  hero: { paddingTop: spacing.xxl, paddingBottom: spacing.lg, borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: colors.onSurface, display: "flex", flexDirection: "column", alignItems: "center" },
  logo: { width: 200, height: 120, objectFit: "contain", marginBottom: spacing.md },
  brandTag: { color: colors.accent, ...fonts.monoBold, fontSize: 12, letterSpacing: 0 },
  body: { padding: spacing.xl, display: "flex", flexDirection: "column" },
  welcome: { ...fonts.display, fontSize: 22, color: colors.onSurface },
  help: { ...fonts.mono, fontSize: 13, color: colors.onSurfaceTertiary, marginTop: 4 },
  label: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: spacing.lg, marginBottom: spacing.xs },
  roleRow: { display: "flex", gap: spacing.sm },
  roleBtn: { flex: 1, borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, padding: spacing.md, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  roleBtnActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  roleText: { ...fonts.monoBold, fontSize: 13, letterSpacing: 0 },
  input: { width: "100%", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.md, ...fonts.mono, fontSize: 16, color: colors.onSurface, outline: "none", boxSizing: "border-box" },
  errBox: { marginTop: spacing.md, backgroundColor: colors.error, padding: spacing.md },
  errText: { color: colors.onError, ...fonts.monoBold, fontSize: 13 },
  cta: { backgroundColor: colors.brandPrimary, padding: 18, marginTop: spacing.lg, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, cursor: "pointer" },
  ctaText: { color: colors.onBrandPrimary, ...fonts.display, fontSize: 16 },
  signOut: { marginTop: spacing.md, background: "transparent", border: "none", cursor: "pointer" },
  signOutText: { ...fonts.monoBold, fontSize: 12, color: colors.onSurfaceTertiary },
};
